import React, { useState, useEffect } from 'react';
import { endpoints } from '../../services/api';
import '../../styles/components/ShareModal.css';
import { getFriendlyError } from '../errorUtils';
import ReactGA from 'react-ga4';


const ShareModal = ({ guide, token, onClose, onUpdate }) => {
  const [emails, setEmails] = useState(guide.shared_emails || []);
  const [newEmail, setNewEmail] = useState('');
  const [isPublic, setIsPublic] = useState(guide.is_public || false);
  const [shareLink, setShareLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    setEmails(guide.shared_emails || []);
    setIsPublic(guide.is_public || false);
    if (guide.share_token) {
      setShareLink(
        `${window.location.origin}/share?token=${guide.share_token}&utm_source=michii&utm_medium=share_link&utm_campaign=guide_share`
      );
    } else {
      setShareLink('');
    }
    setError('');
  }, [guide]);

  const handleTogglePublic = async () => {
    setError('');
    const updatedPublic = !isPublic;
    try {
      const response = await fetch(endpoints.guides.update(guide.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ is_public: updatedPublic }),
      });

      if (!response.ok) throw new Error('Failed to update privacy settings');
      setIsPublic(updatedPublic);
      if (updatedPublic) {
        ReactGA.event({ category: 'Guide', action: 'guide_shared', label: 'made_public' });
      }
      onUpdate && onUpdate({ ...guide, is_public: updatedPublic });
    } catch (err) {
      setError(getFriendlyError(err.message,'Share Guide'));
    }
  };

  const handleAddEmail = async (e) => {
    e.preventDefault();
    if (!newEmail.trim()) return;
    setError('');

    const updatedEmails = [...emails, newEmail.trim()];
    try {
      const response = await fetch(endpoints.guides.update(guide.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shared_emails: updatedEmails }),
      });

      if (!response.ok) throw new Error('Failed to add email');
      setEmails(updatedEmails);
      setNewEmail('');
      ReactGA.event({ category: 'Guide', action: 'guide_shared', label: 'email_invite' });
      onUpdate && onUpdate({ ...guide, shared_emails: updatedEmails });
    } catch (err) {
      setError(getFriendlyError(err.message,'Handle Email'));
    }
  };

  const handleRemoveEmail = async (emailToRemove) => {
    setError('');
    const updatedEmails = emails.filter(e => e !== emailToRemove);
    try {
      const response = await fetch(endpoints.guides.update(guide.id), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shared_emails: updatedEmails }),
      });

      if (!response.ok) throw new Error('Failed to remove email');
      setEmails(updatedEmails);
      onUpdate && onUpdate({ ...guide, shared_emails: updatedEmails });
    } catch (err) {
      setError(getFriendlyError(err.message,'Handle Email'));
    }
  };

  const handleGenerateLink = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(endpoints.guides.shareToken(guide.id), {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) throw new Error('Failed to generate share link');
      const data = await response.json();
      const link = `${window.location.origin}/share?token=${data.share_token}&utm_source=michii&utm_medium=share_link&utm_campaign=guide_share`;
      setShareLink(link);
      ReactGA.event({ category: 'Guide', action: 'guide_shared', label: 'link_generated' });
      onUpdate && onUpdate({ ...guide, share_token: data.share_token });
    } catch (err) {
      setError(getFriendlyError(err.message,'Generate Link'));
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(shareLink);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="share-modal-overlay" onClick={onClose}>
      <div
        className="share-modal-content"
        role="dialog"
        aria-modal="true"
        aria-labelledby="share-modal-title"
        onClick={e => e.stopPropagation()}
      >
        <div className="share-modal-header">
          <div className="share-modal-heading">
            <h2 id="share-modal-title">Share guide</h2>
            <p className="share-modal-guide">{guide.name}</p>
          </div>
          <button className="close-btn" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {error && <p className="modal-error" role="alert">{error}</p>}

        <div className="share-section">
          <h3 className="m-eyebrow">Visibility</h3>
          <label className="switch-container">
            <input
              type="checkbox"
              checked={isPublic}
              onChange={handleTogglePublic}
            />
            <span className="slider"></span>
            <span className="switch-label">
              {isPublic ? 'Anyone can find it in Explore' : 'Only people you invite'}
            </span>
            <span className={`m-badge ${isPublic ? 'm-badge--public' : 'm-badge--private'}`}>
              {isPublic ? 'Public' : 'Private'}
            </span>
          </label>
        </div>

      {!isPublic && (
        <div className="share-section">
          <h3 className="m-eyebrow">Shared with <span className="m-mono">{String(emails.length).padStart(2, '0')}</span></h3>
          <form onSubmit={handleAddEmail} className="email-form">
            <input
              type="email"
              placeholder="Enter Gmail address…"
              value={newEmail}
              onChange={e => setNewEmail(e.target.value)}
              aria-label="Email address to invite"
              required
            />
            <button type="submit" className="lp-btn sm">Add member</button>
          </form>
          <ul className="email-list">
            {emails.length === 0 && <li className="empty-list">No members added yet.</li>}
            {emails.map(email => (
              <li key={email}>
                <span className="email-text">{email}</span>
                <button
                  className="revoke-btn"
                  onClick={() => handleRemoveEmail(email)}
                >
                  Revoke access
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
        <div className="share-section">
          <h3 className="m-eyebrow">Share link</h3>
          {!shareLink ? (
            <button
              className="lp-btn sm"
              onClick={handleGenerateLink}
              disabled={loading}
            >
              {loading ? 'Generating…' : 'Generate share link'}
            </button>
          ) : (
            <div className="link-container">
              <input type="text" value={shareLink} readOnly aria-label="Share link" onFocus={e => e.target.select()} />
              <button className="copy-btn lp-btn sm quiet" onClick={copyToClipboard} aria-label={copied ? 'Link copied' : 'Copy link'}>
                {copied ? 'Copied' : (
                  <>
                    <svg className="copy-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    Copy
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ShareModal;
